import { useEffect, useRef, useState } from "react";
import { Upload, Trash2, Palette, FileText, Image as ImageIcon, Calendar, Loader2 } from "lucide-react";
import { api, API } from "@/lib/api";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";

export default function AdminThemesPage() {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [error, setError] = useState("");
  const fileRef = useRef(null);

  const load = () => api.get("/admin/proposal-themes").then(setItems).catch(() => setItems([]));
  useEffect(() => { load(); }, []);

  const upload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!name.trim()) { alert("Ponele un nombre al tema antes de subir"); return; }
    if (f.size > 20 * 1024 * 1024) { alert("Archivo > 20 MB"); return; }
    setBusy(true); setError("");
    try {
      const fd = new FormData();
      fd.append("name", name);
      fd.append("description", description);
      fd.append("file", f);
      const token = localStorage.getItem("influcas_token");
      const r = await fetch(`${API}/admin/proposal-themes/upload`, { method: "POST", headers: { Authorization: `Bearer ${token}` }, body: fd });
      if (!r.ok) {
        const t = await r.text();
        setError(`Error: ${t}`);
        return;
      }
      setName(""); setDescription("");
      await load();
    } catch (e) { setError("Error de red"); }
    finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const del = async (id) => {
    if (!window.confirm("¿Eliminar tema y todas sus páginas?")) return;
    await api.delete(`/admin/proposal-themes/${id}`);
    load();
  };

  return (
    <div data-testid="admin-themes">
      <div className="flex items-end justify-between mb-6 gap-3 flex-wrap">
        <div>
          <h1 className="font-display text-4xl font-black tracking-tighter mb-1 flex items-center gap-3"><Palette className="w-8 h-8 text-primary" /> Plantillas de Propuestas</h1>
          <p className="text-sm text-muted-foreground">Subí un PDF (o imagen) y se convierte en un tema custom para usar como layout de propuestas.</p>
        </div>
      </div>

      <section className="border border-border rounded-sm p-5 mb-6 bg-secondary/10">
        <h3 className="font-display font-bold text-sm uppercase tracking-widest mb-3">Nuevo tema</h3>
        <div className="grid md:grid-cols-2 gap-3 mb-3">
          <div>
            <label className="text-[10px] uppercase tracking-widest font-bold mb-1 block">Nombre del tema *</label>
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Mi plantilla 2026" className="w-full h-10 px-3 bg-background border border-border rounded-sm text-sm" data-testid="theme-name" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest font-bold mb-1 block">Descripción</label>
            <input value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Brief, formato horizontal..." className="w-full h-10 px-3 bg-background border border-border rounded-sm text-sm" />
          </div>
        </div>
        <label className="block">
          <input ref={fileRef} type="file" accept="application/pdf,image/*" onChange={upload} disabled={busy} className="hidden" data-testid="theme-upload-input" />
          <span className={`flex items-center justify-center gap-2 px-5 h-12 border-2 border-dashed rounded-sm text-sm font-semibold cursor-pointer transition-colors ${busy ? "border-muted-foreground text-muted-foreground" : "border-primary text-primary hover:bg-primary/5"}`}>
            {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Procesando PDF... esto puede tardar varios segundos</> : <><Upload className="w-4 h-4" /> Subir PDF o imagen (máx 20MB)</>}
          </span>
        </label>
        <p className="text-[10px] text-muted-foreground mt-2 italic">Si tu plantilla está en PowerPoint, exportala primero a PDF y subila acá. Cada página del PDF se convierte en una imagen layer del tema.</p>
        {error && <p className="text-sm text-destructive mt-2">{error}</p>}
      </section>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((t) => (
          <article key={t.id} className="border border-border rounded-sm overflow-hidden flex flex-col" data-testid={`theme-card-${t.id}`}>
            {t.pages?.[0]?.media_id && (
              <div className="aspect-video bg-secondary/40 overflow-hidden border-b border-border">
                <img src={`${BACKEND}/api/media/${t.pages[0].media_id}`} alt={t.name} className="w-full h-full object-cover" />
              </div>
            )}
            <div className="p-4 flex-1 flex flex-col gap-2">
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <h3 className="font-display font-black text-lg leading-tight">{t.name}</h3>
                  {t.description && <p className="text-xs text-muted-foreground mt-0.5">{t.description}</p>}
                </div>
                <button onClick={() => del(t.id)} className="p-1.5 border border-destructive/40 text-destructive rounded-sm hover:bg-destructive/10 flex-shrink-0"><Trash2 className="w-3 h-3" /></button>
              </div>
              <div className="flex items-center gap-3 text-[11px] text-muted-foreground">
                <span className="flex items-center gap-1">{t.source_kind === "pdf" ? <FileText className="w-3 h-3" /> : <ImageIcon className="w-3 h-3" />}{t.pages?.length || 0} páginas</span>
                <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{(t.created_at || "").slice(0, 10)}</span>
              </div>
              <div className="flex gap-1 mt-1">
                {[t.detected_accent, t.detected_bg, t.detected_fg].map((c, i) => (
                  <div key={i} className="w-6 h-6 rounded-sm border border-border" style={{ background: c }} title={c} />
                ))}
              </div>
              <p className="text-[10px] font-mono text-muted-foreground mt-1">{t.detected_accent} · {t.detected_bg}</p>
            </div>
          </article>
        ))}
        {items.length === 0 && <p className="text-sm text-muted-foreground border border-dashed border-border p-8 text-center rounded-sm md:col-span-2 lg:col-span-3">No hay plantillas todavía. Subí tu primer PDF arriba.</p>}
      </div>
    </div>
  );
}
